import reportstockledgerService from '../../../services/reportstockledgerService';
import filterService from '../../../services/filterService';

import {
    setReportstockledgerLoading,
    setReportstockledgerError,
    setReportstockledgerList,
    setReportstockledgerCurrent,
    setReportstockledgerPagination,
    setReportstockledgerSort,
    setReportstockledgerFilters,
    setReportstockledgerDefaultFilter,
    setReportstockledgerSettings,
    clearReportstockledgerCurrent,
    clearReportstockledgerError,
    setReportstockledgerFilterLoading,
    changeReportstockledgerFilterCondition,
    addReportstockledgerFilterCondition,
    updateReportstockledgerFilterConditionRule,
    updateReportstockledgerFilterConditionValue,
    removeReportstockledgerFilterCondition,
    setReportstockledgerSelected,
    setReportstockledgerDownloading,
    resetReportstockledgerFiltersAndPagination,

    setReportstockledgerClassicFilters
} from './reportstockledgerSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().reportstockledger.isSettingsInitialized) {
            dispatch(setReportstockledgerLoading(true));
            const response = await reportstockledgerService.getSettings();

            // Set the settings
            dispatch(setReportstockledgerSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReportstockledgerClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setReportstockledgerDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setReportstockledgerError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReportstockledgerLoading(false));
    }
};

export const fetchReportstockledgers = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().reportstockledger;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReportstockledgerLoading(true));
        const response = await reportstockledgerService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReportstockledgerList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchReportstockledgers:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportstockledgerError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportstockledgerLoading(false));
    }
};

export const fetchReportstockledger = (id) => async (dispatch) => {
    try {
        dispatch(setReportstockledgerLoading(true));
        const response = await reportstockledgerService.getOne(id);
        dispatch(setReportstockledgerCurrent(response.data));
    } catch (error) {
        dispatch(setReportstockledgerError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReportstockledgerLoading(false));
    }
};

export const createReportstockledger = (data) => async (dispatch) => {
    try {
        const response = await reportstockledgerService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReportstockledger = (id, data) => async (dispatch) => {
    try {
        const response = await reportstockledgerService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReportstockledger = (id) => async (dispatch) => {
    try {
        dispatch(setReportstockledgerLoading(true));
        const response = await reportstockledgerService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReportstockledgerError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportstockledgerLoading(false));
    }
};

export const downloadReportstockledger = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reportstockledger;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReportstockledgerDownloading(true));
        const response = await reportstockledgerService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setReportstockledgerDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reportstockledgers:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportstockledgerError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportstockledgerDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReportstockledgerPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReportstockledgerSort(sort));
};

export const searchReportstockledgers = (query) => (dispatch, getState) => {
    const currentFilters = getState().reportstockledger.filters;
    dispatch(setReportstockledgerFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReportstockledgerDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReportstockledgerFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReportstockledgerFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReportstockledgerFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReportstockledgerFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReportstockledgerFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReportstockledgerFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reportstockledger.settings;
            dispatch(setReportstockledgerSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReportstockledgerFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReportstockledgerFilterLoading(false));
        dispatch(setReportstockledgerError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReportstockledgerSelected(selectedRows));
    };
};

export const clearCurrentReportstockledger = () => {
    return (dispatch) => {
        dispatch(clearReportstockledgerCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReportstockledgerFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reportstockledger.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReportstockledgerClassicFilters(updatedClassicFilters));
};